<template>
  <Loading v-if="loading" />
  <section v-else class="page">
    <BasePopup
      v-model="isCancelIntegrationPopupOpen"
      :primary-button-action="() => (isCancelIntegrationPopupOpen = false)"
      :secondary-button-action="handleCancelIntegration"
      :title="t('cancel_integration_alert_title')"
      :primary-button-text="t('continue_integration')"
      :secondary-button-text="t('cancel_integration')"
      :loading="deleteIntegrationLoading"
    >
      <template #content>
        <div v-html="cancelIntegrationPopupContent"></div>
      </template>
    </BasePopup>
    <button class="close-button" @click.prevent="close" />
    <Stepper v-model="activeStep" :max-value="2" :show-logo="false">
      <StepperContent :is-visible="activeStep === 1">
        <FullWidthPanel
          v-if="!isCustomSpendingLimitSetupFinished"
          :title="t('step_one_title', { store: currentStore?.name ?? '' })"
          :description="t('step_one_description')"
        >
          <SelectCustomSpendingLimit
            :selected-store="currentStore"
            :min-limit="minLimit"
            :limit-types="spendingLimitTypes"
            @back="close"
            @save="nextStep"
          />
        </FullWidthPanel>
      </StepperContent>
      <StepperContent :is-visible="activeStep === 2">
        <FullWidthPanel
          :class="{ success: isCustomSpendingLimitSetupFinished }"
          :icon="isCustomSpendingLimitSetupFinished ? undefined : 'mdi-alert-outline'"
          :title="
            isCustomSpendingLimitSetupFinished ? t('step_two_success_title') : t('step_two_title')
          "
          :description="
            isCustomSpendingLimitSetupFinished
              ? t('step_two_success_description', {
                  amount: currentStore.spendingLimit?.activeLimit ?? 0,
                })
              : t('step_two_description', { store: currentStore?.name ?? '' })
          "
        >
          <ConfirmCustomSpendingLimit
            v-if="!isCustomSpendingLimitSetupFinished"
            :selected-store="currentStore"
            @back="navigateBack"
            @cancel="isCancelIntegrationPopupOpen = true"
          />
          <CustomSpendingLimitSuccess v-else :selected-store="currentStore" />
        </FullWidthPanel>
      </StepperContent>
    </Stepper>
  </section>
</template>

<script lang="ts">
import { defineComponent } from 'vue'

import StepperContent from '@/components/onboarding/StepperContent.vue'
import Stepper from '@/components/onboarding/Stepper.vue'
import FullWidthPanel from '@/layouts/FullWidthPanel.vue'
import SelectCustomSpendingLimit from '@/components/integration/SelectCustomSpendingLimit.vue'
import ConfirmCustomSpendingLimit from '@/components/integration/ConfirmCustomSpendingLimit.vue'
import CustomSpendingLimitSuccess from '@/components/integration/CustomSpendingLimitSuccess.vue'
import BasePopup from '@/components/tools/BasePopup.vue'
import Loading from '@/components/tools/Loading.vue'

import type { Integration } from '@/store/integrations'
import { IntegrationsMixin } from '@/helpers/mixins/integrationsMixin'
import {
  getIntegrationSpendingLimitRecommendation,
  type Subscription,
  getSubscriptions,
  canceledSubscriptionLogout,
} from '@api/index'

export default defineComponent({
  name: 'ShopifyCustomSpendingLimitView',
  components: {
    FullWidthPanel,
    Stepper,
    StepperContent,
    SelectCustomSpendingLimit,
    ConfirmCustomSpendingLimit,
    CustomSpendingLimitSuccess,
    BasePopup,
    Loading,
  },
  mixins: [IntegrationsMixin],
  data() {
    return {
      activeStep: 1,
      minLimit: 0,
      spendingLimitTypes: {
        recommended: 0,
        enhanced: 0,
      },
      isCancelIntegrationPopupOpen: false,
      loading: false,
      deleteIntegrationLoading: false,
      hasSubscription: false,
      hasStripePayment: false,
    } as {
      activeStep: number
      minLimit: number
      spendingLimitTypes: Record<'recommended' | 'enhanced', number>
      isCancelIntegrationPopupOpen: boolean
      loading: boolean
      deleteIntegrationLoading: boolean
      hasSubscription: boolean
      hasStripePayment: boolean
    }
  },
  computed: {
    integrationList(): Integration[] {
      return this.$store.getters['getIntegrationList']
    },
    userStores(): Integration[] {
      return this.$store.getters['getIntegrationsByPlatform']('shopify')
    },
    currentStore(): Integration {
      return this.$route.query.store
        ? this.userStores.find((store) => store.id === this.$route.query.store) ??
            ({} as Integration)
        : this.$store.getters['getLastShopifyIntegration']
    },
    isCustomSpendingLimitSetupFinished(): boolean {
      if (this.$route.query.store && this.$route.path === '/shopify/success') {
        const selectedStore = this.userStores.find((store) => store.id === this.$route.query.store)
        return (
          !selectedStore?.spendingLimit?.pendingLimit && !!selectedStore?.spendingLimit?.activeLimit
        )
      }

      return false
    },
    hasStripePaymentDetails(): (subscriptions: Subscription[]) => boolean {
      return this.$store.getters['hasStripePaymentDetails']
    },
    hasPaymentDetails(): (subscriptions: Subscription[]) => boolean {
      return this.$store.getters['hasPaymentDetails']
    },
    cancelIntegrationPopupContent(): string | undefined {
      if (this.userStores.length > 1) {
        return this.$t('IntegrationsTable.delete_integration_description', {
          integration: this.currentStore.name,
        })
      }

      if (this.hasStripePayment) {
        return this.t('cancel_integration_alert_stripe_description')
      } else {
        return this.t('cancel_integration_alert_shopify_description')
      }
    },
  },
  async created() {
    this.loading = true

    if (!this.currentStore) {
      return this.$router.push({ name: 'Integrations' })
    }

    try {
      await this.getPaymentDetails()
      await this.setIntegrations()

      if (this.isCustomSpendingLimitSetupFinished) {
        this.activeStep = 2
      }

      await this.setSpendingLimitRecommendations()
    } catch (error) {
      console.error(error)
    } finally {
      this.loading = false
    }
  },
  methods: {
    t(key: string, params?: { [k: string]: string | number }) {
      return this.$t(`ShopifyCustomSpendingLimitView.${key}`, params || {})
    },
    prevStep() {
      this.activeStep--
    },
    nextStep() {
      this.activeStep++
    },
    async close() {
      this.$router.back()

      if (document.documentElement.classList.contains('modal-open')) {
        document.documentElement.classList.remove('modal-open')
      }
    },
    async navigateBack() {
      this.prevStep()
      await this.setSpendingLimitRecommendations()
    },
    async getPaymentDetails() {
      const subscriptions = await getSubscriptions()
      this.hasSubscription = this.hasPaymentDetails(subscriptions)
      this.hasStripePayment = this.hasStripePaymentDetails(subscriptions)
    },
    async handleCancelIntegration() {
      this.deleteIntegrationLoading = true

      await this.deleteIntegration(this.currentStore.id)
      await this.getPaymentDetails()

      if (!this.hasSubscription) {
        await canceledSubscriptionLogout()
        window.location.href = '/canceled'
        return
      }

      this.deleteIntegrationLoading = false
      this.isCancelIntegrationPopupOpen = false
      await this.close()
    },
    async setSpendingLimitRecommendations() {
      this.loading = true

      const { data } = await getIntegrationSpendingLimitRecommendation(this.currentStore.id)

      this.spendingLimitTypes = {
        recommended: data.recommended,
        enhanced: data.enhanced,
      }
      this.minLimit = data.minimum

      this.loading = false
    },
  },
  watch: {
    activeStep(updated, previous) {
      if (updated !== previous) window.scrollTo(0, 0)
    },
  },
})
</script>

<style lang="scss" scoped>
@import '~vuetify/settings';

.page {
  height: 100%;
  min-height: 100vh;
  background: url('@/assets/backgrounds/full-width-popup-background.svg') no-repeat top left/cover
    var(--gray-light-F1);
  padding-bottom: 20px;

  @media #{map-get($display-breakpoints, 'md-and-up')} {
    padding-bottom: 40px;
  }
}

.close-button {
  z-index: 2;
}

.success {
  :deep(.panel__title) {
    color: var(--ui-green);
  }
}
</style>
